import { gql, type TypedDocumentNode } from '@apollo/client';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { CardImg } from '@sorare/core/src/components/card/CardImg.tsx';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { PlaceholderSlot } from '@sorare/marketplace/src/components/commonCard/RevealPackCardsDialog/CollectionAnimation/CollectionAnimationClub/PlaceholderSlot';

import { CardsCountBadge } from '#src/components/collections/CardsCountBadge/index.tsx';

import type { CollectionSlot_userCardCollectionSlot } from './__generated__/index.graphql.ts';

const Root = styled.div`
  cursor: pointer;
  position: relative;
  isolation: isolate;
  border: 1px solid var(--c-nd-50);
  border-radius: var(--double-unit);
  aspect-ratio: var(--card-aspect-ratio);
`;

type Props = {
  userCardCollectionSlot: Nullable<CollectionSlot_userCardCollectionSlot>;
  small?: boolean;
  displayPlayerName?: boolean;
};

export const CollectionSlot = ({
  userCardCollectionSlot,
  small = false,
  displayPlayerName = false,
}: Props) => {
  const { highlightedCardCollectionCard, cardCollectionCardsCount, slot } =
    userCardCollectionSlot || {};
  const isLaptopAndAbove = useIsLaptop();
  const openDialog = useOpenDialog();
  if (cardCollectionCardsCount === 0) {
    const slotId = userCardCollectionSlot?.slot.id;
    return (
      <ConditionalWrapper
        Wrapper={Root}
        props={{
          onClick: () => openDialog(DialogKey.card, slotId!),
        }}
        wrap={slotId !== undefined}
      >
        <PlaceholderSlot
          slot={userCardCollectionSlot}
          small={small}
          displayPlayerName={displayPlayerName}
        />
      </ConditionalWrapper>
    );
  }
  return (
    <CardsCountBadge
      size="small"
      userCardCollectionSlot={userCardCollectionSlot}
    >
      <ButtonBase
        disabled={!highlightedCardCollectionCard?.anyCard?.slug}
        onClick={() => {
          if (highlightedCardCollectionCard?.anyCard?.slug) {
            openDialog(
              DialogKey.card,
              highlightedCardCollectionCard.anyCard.slug
            );
          }
        }}
      >
        {!!slot?.cardPictureUrl && (
          <CardImg
            width={isLaptopAndAbove ? 160 : 80}
            src={slot.cardPictureUrl}
          />
        )}
      </ButtonBase>
    </CardsCountBadge>
  );
};

CollectionSlot.fragments = {
  userCardCollectionSlot: gql`
    fragment CollectionSlot_userCardCollectionSlot on UserCardCollectionSlot {
      slug
      slot {
        id
        cardPictureUrl
      }
      highlightedCardCollectionCard {
        id
        anyCard {
          slug
        }
      }
      cardCollectionCardsCount
      ...PlaceholderSlot_userCardCollectionSlot
      ...CardsCountBadge_userCardCollectionSlot
    }
    ${PlaceholderSlot.fragments.userCardCollectionSlot}
    ${CardsCountBadge.fragments.userCardCollectionSlot}
  ` as TypedDocumentNode<CollectionSlot_userCardCollectionSlot>,
};
